import { ApolloClient } from 'apollo-client'
import * as React from 'react'
import { Mutation, MutationFunction } from 'react-apollo'

import {
  InitialParentSignUpForm,
  InitialParentSignUpFormSSO,
  LoginForm,
  LoginFormSSO,
} from 'main/components/common/AuthModal'
import FinalParentSignUpForm, {
  isFinalParentSignUpFormRequired,
} from 'main/components/common/AuthModal/parent/FinalParentSignUpForm'
import IntermediateParentSignUpForm, {
  isIntermediateParentSignUpFormRequired,
} from 'main/components/common/AuthModal/parent/IntermediateParentSignUpForm'
import Loader from 'main/components/common/Loader'
import { AppContext, IAppContext } from 'main/framework'
import { CredentialsFields } from 'main/gql/credentials'
import {
  LEAD_BOOKMARK_MUTATION,
  LEAD_UNBOOKMARK_MUTATION,
  LeadBookmark,
  LeadBookmarkVariables,
  LEADS_FOR_PARENT_QUERY,
  LeadUnbookmark,
  LeadUnbookmarkVariables,
} from 'main/gql/lead'
import { User_user } from 'main/gql/user'
import { Flags, useFeatureFlag } from 'main/hooks/useFeatureFlag'
import { login } from 'main/util/auth'
import { UserSignUpSource } from 'main/util/user'

import { ITrackData, track } from './track'
import { Step } from './types'

interface IProps {
  client: ApolloClient<{}>
  trackData: ITrackData
  isBookmarked?: boolean
  schoolId: number
  step: Step
  user: User_user | null
  onAuth?: (credentials: CredentialsFields) => void
  onChangeStep: (step: Step) => void
  onForgotPassword: () => void
  onRequestClose: () => void
}

export default function Content(props: IProps) {
  const { trackData, step, user, onChangeStep, onRequestClose } = props

  switch (step) {
    case Step.Login:
      return handleBookmark(props, renderLoginForm)
    case Step.ParentStart:
      return handleBookmark(props, renderInitialParentSignUpForm)
    case Step.ParentIntermediate:
      if (!user) return <Loader />
      return (
        <IntermediateParentSignUpForm
          user={user}
          onComplete={() => {
            track('completed_sign_up_intermediate', trackData)

            isFinalParentSignUpFormRequired(user)
              ? onChangeStep(Step.ParentFinal)
              : onRequestClose()
          }}
          cta="Save school to favorites"
        />
      )
    case Step.ParentFinal:
      if (!user) return <Loader />
      return (
        <FinalParentSignUpForm
          user={user}
          onComplete={() => {
            track('completed_sign_up_final', trackData)
            onRequestClose()
          }}
        />
      )
    default:
      return null
  }
}

function renderLoginForm(props: IProps, mutate: MutationFunction<any, { schoolId: number }>) {
  const { trackData, schoolId, onForgotPassword, onChangeStep } = props

  const appContext = React.useContext(AppContext)
  const ssoFlag = useFeatureFlag(Flags.SSO)
  const isSSO = ssoFlag.enabled()

  if (isSSO) {
    return (
      <LoginFormSSO
        segmentData={{
          event: 'completed_login',
          data: trackData,
        }}
        extraData={{
          schoolId,
        }}
      />
    )
  } else {
    return (
      <LoginForm
        onComplete={credentials => {
          track('completed_login', trackData)
          handleCompleteLogin(props, credentials, mutate, appContext)
        }}
        onForgotPassword={() => onForgotPassword()}
        onRequestSignUp={() => onChangeStep(Step.ParentStart)}
      />
    )
  }
}

function renderInitialParentSignUpForm(
  props: IProps,
  mutate: MutationFunction<any, { schoolId: number }>,
) {
  const { trackData, schoolId, onChangeStep } = props

  const appContext = React.useContext(AppContext)
  const ssoFlag = useFeatureFlag(Flags.SSO)
  const isSSO = ssoFlag.enabled()

  if (isSSO) {
    return (
      <InitialParentSignUpFormSSO
        googleOptimize={{
          submitButton: 'bookmarkSignUpSubmitButton',
          subtext: 'bookmarkSignUpSubtext',
        }}
        subtext="Create an account to save favorite schools"
        source={UserSignUpSource.ParentBookmarkModal}
        segmentData={{
          event: 'completed_parent_sign_up',
          data: { ...trackData, schoolId },
        }}
      />
    )
  } else {
    return (
      <InitialParentSignUpForm
        googleOptimize={{
          submitButton: 'bookmarkSignUpSubmitButton',
          subtext: 'bookmarkSignUpSubtext',
        }}
        source={UserSignUpSource.ParentBookmarkModal}
        subtext="Create an account to save favorite schools"
        trackingData={{ schoolId }}
        onComplete={credentials => {
          track('completed_sign_up', trackData)
          handleCompleteLogin(props, credentials, mutate, appContext)
        }}
        onRequestLogin={() => onChangeStep(Step.Login)}
      />
    )
  }
}

function handleBookmark(
  props: IProps,
  renderFormComponent: (
    props: IProps,
    mutate: MutationFunction<LeadBookmark | LeadUnbookmark, LeadBookmarkVariables>,
  ) => JSX.Element,
) {
  const { isBookmarked } = props

  if (isBookmarked) {
    return (
      <Mutation<LeadUnbookmark, LeadUnbookmarkVariables>
        mutation={LEAD_UNBOOKMARK_MUTATION}
        refetchQueries={[{ query: LEADS_FOR_PARENT_QUERY }]}
      >
        {mutate => renderFormComponent(props, mutate)}
      </Mutation>
    )
  } else {
    return (
      <Mutation<LeadBookmark, LeadBookmarkVariables>
        mutation={LEAD_BOOKMARK_MUTATION}
        refetchQueries={[{ query: LEADS_FOR_PARENT_QUERY }]}
      >
        {mutate => renderFormComponent(props, mutate)}
      </Mutation>
    )
  }
}

function handleCompleteLogin(
  props: IProps,
  credentials: CredentialsFields,
  mutate: any,
  context: IAppContext,
) {
  const { client, schoolId, onAuth } = props

  login(client, credentials, context).then(() => {
    if (onAuth) {
      onAuth(credentials)
    }
  })

  mutate({ variables: { schoolId } })
  return advanceParentPostAuth(props, credentials)
}

function advanceParentPostAuth(props: IProps, credentials: CredentialsFields) {
  const { onChangeStep, onRequestClose } = props
  const { user } = credentials

  if (isIntermediateParentSignUpFormRequired(user)) {
    onChangeStep(Step.ParentIntermediate)
  } else if (isFinalParentSignUpFormRequired(user)) {
    onChangeStep(Step.ParentFinal)
  } else {
    onRequestClose()
  }
}
